import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import appConfig from 'app-config';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { withModalContext } from '@jutro/components';
import { E1PLoader } from 'e1p-capability-policyjob-react';
import { JobUtil } from '@xengage/gw-portals-util-js';
import { TranslatorContext } from '@jutro/locale';
import { PolicyChangeService } from 'gw-capability-gateway';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { TransactionViewFlowUtil } from 'e1p-portals-util-js';
import { e1pDateUtil } from 'e1p-capability-hooks';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { commonMessages as e1pCommonMessages, policyChangeMessages } from 'e1p-platform-translations';
import UnderwritingComponent from '../../Components/UnderwritingComponent/UnderwritingComponent';
import gatewayMessages from '../../gateway.messages';
import metadata from './Summary.metadata.json5';
import messages from './Summary.messages';
import styles from './Summary.module.scss';

class EndorsementSummaryWithoutModalContext extends Component {
    static contextType = TranslatorContext;

    static propTypes = {
        authHeader: PropTypes.shape({
            Authorization: PropTypes.string
        }),
        fromAccountLanding: PropTypes.shape({
            quoteDetailsData: PropTypes.shape({
                jobNumber: PropTypes.string.isRequired,
                loadPolicyChangeSummary: PropTypes.shape({}).isRequired,
                updateJobSummary: PropTypes.func.isRequired
            })
        }).isRequired,
        history: PropTypes.shape({
            push: PropTypes.func.isRequired
        }).isRequired,
        authUserData: PropTypes.shape({
            userType: PropTypes.string
        }),
    };

    static defaultProps = {
        authHeader: undefined,
        authUserData: undefined
    }

    state = {
        showContinuePolicyChangeButton: false,
        canWithdraw: false,
        isLoading: true
    };

    componentDidMount() {
        this.canWithdraw();
        this.getShowContinuePolicyChange();
    };

    getShowContinuePolicyChange = async () => {
        const { authUserData } = this.props;
        const showContinuePolicyChangeButton = authUserData?.permissions_Ext.includes('createpolchange');

        this.setState({ showContinuePolicyChangeButton, isLoading: false });
    };

    canWithdraw = () => {
        const {
            fromAccountLanding: {
                quoteDetailsData: { loadPolicyChangeSummary: policyChange }
            },
            authUserData
        } = this.props;

        const withdrawPermission = authUserData?.permissions_Ext.includes('withdraw');

        if (!_.isEmpty(policyChange)) {
            const translator = this.context;

            if (
                withdrawPermission
                && policyChange.status
                !== translator({
                    id: 'typekey.PolicyPeriodStatus.Bound',
                    defaultMessage: 'Bound'
                })
                && !policyChange.closeDate
            ) {
                this.setState({ canWithdraw: true });
            }
        }
    };

    updateWithDrawPolicyChange = (jobStatusCode, policyChange) => {
        const translator = this.context;
        const status = translator({
            id: `typekey.PolicyPeriodStatus.${jobStatusCode}`,
            defaultMessage: jobStatusCode
        });

        _.set(policyChange, 'status', status);
        _.set(policyChange, 'statusCode', jobStatusCode);
        this.updateJobSummary(policyChange);
    };

    onContinueTransaction = () => {
        const { lobEndorsementURL, endorsementProducts } = appConfig;
        const {
            fromAccountLanding: {
                quoteDetailsData: { loadPolicyChangeSummary: policyChange }
            },
            history
        } = this.props;

        const productCode = _.get(policyChange, 'productCode');

        if (endorsementProducts.includes(productCode)) {
            history.push(lobEndorsementURL[productCode], {
                jobNumber: policyChange.jobNumber,
                policyNumber: policyChange.policy.policyNumber
            });
        } else {
            JobUtil.openJobInXCenter(policyChange.jobNumber);
        }
    };

    onViewTransaction = () => {
        const {
            fromAccountLanding: {
                quoteDetailsData: { loadPolicyChangeSummary: policyChange }
            },
            history
        } = this.props;
        const productCode = _.get(policyChange, 'productCode');
        const productMap = {
            PersonalAuto_EA: 'personalAuto_EA',
            Homeowners_EH: 'homeowners_EH',
            PersonalUmbrella_EU: 'personalUmbrella_EU'
        };
        const policyType = policyChange.policy.lobs[productMap[productCode]].policyType_Ext;

        return TransactionViewFlowUtil.goToTransactionSpecificViewOnlyFlow(
            history, policyChange?.jobNumber, policyType,
            policyChange?.type, policyChange?.productCode,
            policyChange?.policy?.policyNumber
        );
    };

    onWithdrawTransaction = () => {
        const translator = this.context;
        const {
            fromAccountLanding: {
                quoteDetailsData: { loadPolicyChangeSummary: policyChange }
            },
            authHeader
        } = this.props;

        this.props.modalContext.showConfirm({
            title: policyChangeMessages.withdrawPolicyChange,
            message: policyChangeMessages.areYouSureToWithdrawPolicyChange,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: translator(e1pCommonMessages.withdrawTransaction, { transaction: 'POLICY CHANGE' }),
        }).then((result) => {
            if (result === 'cancel') {
                return;
            }

            PolicyChangeService.withdrawPolicyChangeByPolicyChangeNumber(
                policyChange.jobNumber,
                authHeader
            ).then(
                () => {
                    this.updateWithDrawPolicyChange('Withdrawn', policyChange);
                },
                () => {
                    this.props.modalContext.showAlert({
                        title: gatewayMessages.modalError,
                        message: messages.failedToWithdrawPolicyChange,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.close
                    }).catch(_.noop);
                }
            );
        }, _.noop);
    };

    getTransactionActionContent = () => {
        const translator = this.context;

        return {
            viewContent: translator(messages.viewPolicyChange),
            withDrawContent: translator(policyChangeMessages.withdrawPolicyChange),
            continueContent: translator(messages.continuePolicyChange)
        };
    };

    getPolicyChangeToProceed = () => {
        const { canWithdraw, showContinuePolicyChangeButton } = this.state;
        const {
            fromAccountLanding: {
                quoteDetailsData: { loadPolicyChangeSummary: policyChange }
            }
        } = this.props;
        const translator = this.context;

        if (
            policyChange
            && (policyChange.statusCode === 'Withdrawn' || policyChange.statusCode === 'Bound')
        ) {
            return {
                isContinueTransaction: false,
                isWithdrawTransaction: false,
                isViewTransaction: policyChange.canUserView
            };
        }

        const additionalConditionToShowContinueButton = !policyChange.closeDate
            && policyChange.status
            !== translator({
                id: 'typekey.PolicyPeriodStatus.Bound',
                defaultMessage: 'Bound'
            });

        return {
            isContinueTransaction:
                showContinuePolicyChangeButton
                && additionalConditionToShowContinueButton
                && !policyChange.isEditLocked,
            isWithdrawTransaction: canWithdraw,
            isViewTransaction: policyChange.canUserView
        };
    };

    getPolicyLinkVisible = () => {
        const {
            fromAccountLanding: {
                quoteDetailsData: { loadPolicyChangeSummary: policyChange }
            }
        } = this.props;

        const policyNumber = _.get(policyChange, 'policy.latestPeriod.policyNumber', 'Unassigned');

        return policyNumber !== 'Unassigned';
    };

    updateJobSummary = (policyChange) => {
        const {
            fromAccountLanding: {
                quoteDetailsData: { updateJobSummary }
            }
        } = this.props;

        if (updateJobSummary) {
            updateJobSummary(policyChange);
        }
    };

    render() {
        const {
            fromAccountLanding: {
                quoteDetailsData: { loadPolicyChangeSummary: policyChange }
            }
        } = this.props;
        const { isLoading } = this.state;

        if (_.isEmpty(policyChange)) {
            return <E1PLoader loaded={!isLoading} />;
        }

        const policyNumber = _.get(policyChange, 'policy.latestPeriod.policyNumber');

        const overrides = {
            '@field': {
                readOnly: true,
                autoComplete: false
            },
            transactionActions: {
                transactionActionContent: this.getTransactionActionContent(),
                transactionVisibleActions: this.getPolicyChangeToProceed()
            },
            policyLinkDiv: {
                visible: this.getPolicyLinkVisible()
            },
            policyInfoLink: {
                content: policyNumber,
                to: `/policies/${policyNumber}/summary`
            },
            summaryPolicyInceptionId: {
                value: e1pDateUtil.convertToUTCForDateWithoutTZ(_.get(policyChange, 'policy.latestPeriod.effectiveDate'))
            },
            policyChangeTermEffectiveDate: {
                value: e1pDateUtil.convertToUTCForDateWithoutTZ(_.get(policyChange, 'termEffectiveDate'))
            },
            quotedBoundContainer: {
                visible: policyChange.statusCode === 'Quoted' || policyChange.statusCode === 'Bound'
            },
            producerCodeOfRecordOrgId: {
                value: `${_.get(
                    policyChange,
                    'policy.latestPeriod.producerCodeOfRecordOrg'
                )} (${_.get(policyChange, 'policy.latestPeriod.externalID_Ext')})`
            },
            producerOfServiceId: {
                value: `${_.get(
                    policyChange,
                    'policy.latestPeriod.producerCodeOfServiceOrg'
                )} (${_.get(policyChange, 'policy.latestPeriod.externalID_Ext')})`
            },
            preferredUnderwriterId: {
                value: _.get(
                    policyChange,
                    'policy.latestPeriod.assignedUWName_Ext', ''
                )
            },
            policyChangeNotificationContainer: {
                visible: _.get(
                    policyChange,
                    'hasPreemptions_Ext',
                    false
                )
            }
        };
        const resolvers = {
            resolveClassNameMap: styles,
            resolveComponentMap: {
                underwritingcomponent: UnderwritingComponent
            },
            resolveCallbackMap: {
                onContinueTransaction: this.onContinueTransaction,
                onWithdrawTransaction: this.onWithdrawTransaction,
                onViewTransaction: this.onViewTransaction
            }
        };
        const readValue = (id, path) => readViewModelValue(metadata.pageContent, policyChange, id, path, overrides);

        return (
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={policyChange}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                resolveValue={readValue}
            />
        );
    }
}

const EndorsementSummary = withModalContext(EndorsementSummaryWithoutModalContext);

export default withAuthenticationContext(EndorsementSummary);
